<template>
  <div
    class="vendor_center-main vendor-center--glass"
    :class="[
      viewportTierClass,
      {
        'vendor-center--compact': isCompactViewport,
        'vendor-center--mobile': isMobileViewport
      }
    ]"
  >
    <VendorHeader
      :vendor-filter="vendorFilter"
      :vendor-role-filter="vendorRoleFilter"
      :vendor-scope="vendorScope"
      :vendors="vendors"
      :global-search-label="globalSearchLabel"
      @filter-changed="handleFilterChanged"
      @vendor-role-changed="handleVendorRoleFilterChanged"
      @vendor-scope-changed="handleVendorScopeChange"
      @global-search-selected="handleGlobalSearchSelected"
      @global-search-cleared="handleGlobalSearchCleared"
    />
      <div class="vendor_center-content">
      <div
        class="vendor_center-sidemenu-wrap"
        :class="{ resizing: resizing }"
        :style="sidemenuWrapStyle"
      >
        <VendorSideMenu
          v-if="isSideMenuVisible"
          :vendors="sideMenuVendors"
          :selected-vendor="selectedVendor"
          :vendor-scope="vendorScope"
          :vendor-filter="vendorFilter"
          :vendor-role-filter="vendorRoleFilter"
          :searchQuery="searchQuery"
          :loading="loading"
          :totalCount="totalVendorsCount"
          :loadingMore="loadingMoreVendors"
          :currentPage="vendorsPage"
          :recordsPerPage="vendorsPerPage"
          show-presence
          @vendor-selected="handleVendorSelected"
          @search-changed="handleSearchChanged"
          @load-more="handleLoadMoreVendors"
          @page-changed="handleVendorPageChange"
          @hide-side-menu="hideSideMenu"
        />
      </div>
      <div
        v-if="isSideMenuVisible"
        class="vendor_center-resizer"
        role="separator"
        aria-label="Resize vendor list"
        @mousedown="startResize"
      ></div>
      <VendorPreviewList
        ref="vendorPreviewList"
        :selected-vendor="selectedVendor"
        :vendor-filter="vendorFilter"
        :vendor-scope="vendorScope"
        :tracked-vendors-count="filteredVendors.length"
        :transactions="transactionsForPreview"
        :loading-transactions="loadingBills || loadingPayBills"
        :currentPage="currentPage"
        :itemsPerPage="itemsPerPage"
        :currentViewFilter="getViewFilterValue(currentView)"
        :dateFilter="getDateFilterValue(dateFilter)"
        :typeFilter="getTypeFilterValue(billTypeFilter)"
        :accountsMap="accountsMap"
        :vendors="vendors"
        :show-side-menu="isSideMenuVisible"
        @page-change="handlePageChange"
        @items-per-page-change="handleItemsPerPageChange"
        @view-filter-changed="handleViewFilterChanged"
        @date-filter-changed="handleDateFilterChanged"
        @type-filter-changed="handleTypeFilterChanged"
        @export-csv="handleExportToCsv"
        @toggle-side-menu="toggleSideMenu"
      />
    </div>

    <!-- Vendor Transactions Modal -->
    <VendorTransactionsModal
      :show="showTransactionsModal"
      title="Vendor Transactions Preview"
      description="Review vendor transactions before exporting to Excel"
      :transactions="vendorTransactions"
      :vendors="filteredVendors"
      :accountsMap="accountsMap"
      :loading="loadingTransactions"
      :error="transactionsError"
      @close="closeTransactionsModal"
      @export="confirmExportToExcel"
    />
  </div>
</template>

<script>
import '../styles/VendorCenter.css';
import '../styles/vendor-center-glass.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import VendorHeader from '../components/VendorHeader.vue';
import VendorSideMenu from '../components/VendorSideMenu.vue';
import VendorPreviewList from '../components/VendorPreviewList.vue';
import VendorTransactionsModal from '../components/VendorTransactionsModal.vue';
import { fetchVendorTransactions, exportVendorsWithTransactionsToExcel } from '../utils/vendorListExportFile.js';
export default {
  name: 'VendorCenter',
  components: { VendorHeader, VendorSideMenu, VendorPreviewList, VendorTransactionsModal },
  data() {
    return {
      vendors: [],
      selectedVendor: null,
      loading: false,
      error: '',
      // Advanced pagination for vendors (handles 10k+ records)
      vendorsPage: 1,
      vendorsPerPage: 100, // Optimized batch size
      loadingMoreVendors: false,
      hasMoreVendors: true,
      totalVendorsCount: 0, // Total count from server
      vendorFilter: 'Active',
      vendorRoleFilter: '',
      // Advanced caching with TTL (Time To Live)
      vendorsCache: new Map(), // Key: cacheKey, Value: { data, timestamp, ttl }
      cacheTTL: 5 * 60 * 1000, // 5 minutes cache TTL
      lastSearchQuery: null, // Initialize as null to detect first change
      lastVendorFilter: 'Active',
      lastVendorRoleFilter: '',
      // Retry configuration
      maxRetries: 3,
      retryDelay: 1000, // 1 second
      // Cache cleanup interval
      cacheCleanupInterval: null,
      maxCacheSize: 50, // Maximum number of cache entries
      loadingSpecificPage: false, // Flag to track if loading a specific page
      bills: [],
      payBills: [],
      cheques: [],
      vendorCredits: [],
      cashPurchases: [],
      expenses: [],
      cashExpenses: [],
      creditCardExpenses: [],
      currentView: 'All Transactions',
      billTypeFilter: 'All Bills',
      dateFilter: 'All',
      accounts: [],
      accountsMap: {},
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      // Performance optimization: Debouncing and request cancellation
      debounceTimer: null,
      currentRequestController: null,
      // Memoization cache for computed values
      computedCache: {
        normalizedTransactions: null,
        lastDataHash: null
      },
      // Modal state
      showTransactionsModal: false,
      vendorTransactions: [],
      loadingTransactions: false,
      transactionsError: '',
      sidemenuWidth: 260,
      resizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,
      showSideMenu: true,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
      vendorScope: 'individual',
      globalSearchLabel: '',
      globalSearchVendorId: null,
      loadingBills: false,
      loadingPayBills: false,
      onlineVendorIds: [],
      onVendorPresenceEvent: null
    };
  },
  computed: {
    companyId() {
      return Number(this.$route.params.id || this.$route.params.companyId);
    },
    sideMenuVendors() {
      const online = new Set(this.onlineVendorIds.map(Number));
      return this.vendors.map((vendor) => ({
        ...vendor,
        is_online: online.has(Number(vendor.id))
      }));
    },
    isCompactViewport() {
      return this.viewportWidth < 1280;
    },
    isMobileViewport() {
      return this.viewportWidth < 768;
    },
    viewportTierClass() {
      if (this.viewportWidth >= 1440) return 'vendor-center--desktop';
      if (this.viewportWidth >= 1024) return 'vendor-center--laptop';
      if (this.viewportWidth >= 768) return 'vendor-center--tablet';
      return 'vendor-center--mobile';
    },
    sidemenuMinWidth() {
      return this.isCompactViewport ? 200 : 220;
    },
    sidemenuMaxWidth() {
      return this.isCompactViewport ? 420 : 480;
    },
    sidemenuWrapStyle() {
      if (!this.isSideMenuVisible) {
        return { width: '0px', minWidth: '0px', maxWidth: 'none' };
      }
      if (this.isMobileViewport) {
        return { width: '100%', minWidth: '0', maxWidth: '100%' };
      }
      return {
        width: `${this.sidemenuWidth}px`,
        minWidth: `${this.sidemenuMinWidth}px`,
        maxWidth: `${this.sidemenuMaxWidth}px`
      };
    },
    isSideMenuVisible() {
      return this.vendorScope !== 'all' && this.showSideMenu;
    },
    filteredVendors() {
      // Compute filtered vendors directly
      let filtered = this.vendors;
      if (this.vendorFilter === 'Active') {
        filtered = filtered.filter(v => v.active !== false);
      } else if (this.vendorFilter === 'Inactive') {
        filtered = filtered.filter(v => v.active === false);
      }
      if (this.vendorRoleFilter) {
        filtered = filtered.filter((v) => v.vendorRole === this.vendorRoleFilter);
      }
      // If 'All' is selected, don't filter by status (show all vendors)
      if (this.searchQuery) {
        filtered = filtered.filter(v => (v.vendorName || v.name || '').toLowerCase().includes(this.searchQuery.toLowerCase()));
      }
      return filtered;
    },
    transactionsForPreview() {
      let transactions = [...this.normalizedTransactions];
      if (this.vendorScope === 'individual' && this.selectedVendor) {
        transactions = transactions.filter(
          (t) => String(t.vendorId) === String(this.selectedVendor.id)
        );
      } else if (this.vendorScope === 'all' && this.globalSearchVendorId) {
        transactions = transactions.filter(
          (t) => String(t.vendorId) === String(this.globalSearchVendorId)
        );
      } else if (this.vendorFilter === 'Active' || this.vendorFilter === 'Inactive') {
        const allowedVendorIds = this.filteredVendors.map((v) => String(v.id));
        transactions = transactions.filter(
          (t) => t?.vendorId != null && allowedVendorIds.includes(String(t.vendorId)),
        );
      }
      return transactions;
    },
    normalizedTransactions() {
      // Check cache first
      const dataHash = JSON.stringify({
        bills: this.bills.map(b => ({ id: b.id, billNo: b.billNo })),
        payBills: this.payBills.map(pb => ({ id: pb.id, no: pb.no })),
        cheques: this.cheques.map(c => ({ id: c.id, chequeNo: c.chequeNo })),
        vendorCredits: this.vendorCredits.map(vc => ({ id: vc.id, creditNoteNo: vc.creditNoteNo })),
        cashPurchases: this.cashPurchases.map(cp => ({ id: cp.id })),
        expenses: this.expenses.map(e => ({ id: e.id })),
        cashExpenses: this.cashExpenses.map(e => ({ id: e.id })),
        creditCardExpenses: this.creditCardExpenses.map(e => ({ id: e.id })),
      });
      
      if (this.computedCache.normalizedTransactions && this.computedCache.lastDataHash === dataHash) {
        return this.computedCache.normalizedTransactions;
      }
      const normalize = t => {
        // Bill
        if (t.billNo || (t.type === 'Bill' && t.no)) {
          const billNo = t.billNo || t.no;
          const vendorId = t.vendorId || t.vendor_id || t.vendor?.id;
          const vendor = this.vendors.find((v) => String(v.id) === String(vendorId));
          return {
            id: t.id,
            enterBillId: t.id,
            billNo,
            date: t.date,
            type: 'Bill',
            number: billNo,
            memo: t.comments || '-',
            dueDate: t.dueDate || null,
            amount: t.total ?? t.originalAmount,
            status: t.status || '-',
            vendorId,
            vendorName: t.vendorName || vendor?.vendorName || vendor?.name || '',
            account: t.bankAccount || '-',
            billAttachments: Array.isArray(t.attachments) ? t.attachments : [],
            isBankRecorded: !!t.isBankRecorded,
            bankRecord: t.bankRecord || null,
            raw: t
          };
        }
        // Pay Bill
        if (t.no && (t.type === 'Pay Bill' || t.payBillNo || t.paymentMethod)) {
          const vendorId = t.vendorId || t.vendor_id || t.vendor?.id;
          const vendor = this.vendors.find((v) => String(v.id) === String(vendorId));
          return {
            id: t.id,
            vendorPayBillId: t.id,
            payBillNo: t.no || t.payBillNo,
            date: t.date,
            type: 'Pay Bill',
            number: t.no,
            memo: t.comments || '-',
            dueDate: '',
            amount: t.total,
            status: 'Received',
            vendorId,
            vendorName: t.vendorName || vendor?.vendorName || vendor?.name || '',
            account: t.payFromAccountId,
            paymentMethod: t.paymentMethod || t.payMethod || 'Cash',
            isBankRecorded: !!t.isBankRecorded,
            bankRecord: t.bankRecord || null,
            raw: t
          };
        }
        // Cheque
        if (t.chequeNo) {
          return {
            id: t.id,
            date: t.date,
            type: 'Cheque',
            number: t.chequeNo,
            memo: t.comments || '-',
            dueDate: t.dueDate || '',
            amount: t.total,
            status: t.status || '',
            vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
            account: t.bankAccount || '',
            raw: t
          };
        }
        // Vendor Credit Note
        if (t.creditNoteNo) {
          return {
            id: t.id,
            date: t.date,
            type: 'Vendor Credit Note',
            number: t.creditNoteNo,
            memo: t.comments || '-',
            dueDate: t.dueDate || '',
            amount: t.total,
            status: t.status || '',
            vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
            account: t.bankAccount || '',
            raw: t
          };
        }
        // Cash Purchase
        if (t.type === 'Cash Purchase' || t.cashPurchaseNo) {
          return {
            id: t.id,
            date: t.date,
            type: 'Cash Purchase',
            number: t.number || t.cashPurchaseNo,
            memo: t.comments || '-',
            dueDate: t.dueDate || '',
            amount: t.total,
            status: t.status || '',
            vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
            account: t.payFrom || '',
            raw: t
          };
        }
        // Direct vendor expense (cash or credit card charge)
        if (
          t.expenseKind === 'Cash Expense'
          || t.expenseKind === 'Credit Card Expense'
          || t.sourceType === 'cash-expense'
          || t.sourceType === 'credit-card-expense'
        ) {
          const vendorId = t.vendorId || t.vendor_id || t.vendor?.id;
          const vendor = this.vendors.find((v) => String(v.id) === String(vendorId));
          const expenseKind = t.expenseKind
            || (t.sourceType === 'cash-expense' ? 'Cash Expense' : 'Credit Card Expense');
          const prefix = expenseKind === 'Cash Expense' ? 'CE' : 'CCE';
          return {
            id: t.id,
            date: t.date,
            type: 'Expense',
            expenseKind,
            number: t.number || t.no || `${prefix}-${String(t.id).padStart(4, '0')}`,
            memo: t.comments || '-',
            dueDate: '',
            amount: t.total ?? t.originalAmount,
            status: 'Paid',
            vendorId,
            vendorName: t.vendorName || vendor?.vendorName || vendor?.name || '',
            account: t.payFrom || '',
            raw: t,
          };
        }
        // Expense
        if (t.type === 'Expense' || t.expenseNo) {
          return {
            id: t.id,
            date: t.date,
            type: 'Expense',
            number: t.number || t.expenseNo,
            memo: t.comments || '-',
            dueDate: t.dueDate || '',
            amount: t.total,
            status: t.status || '',
            vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
            account: t.payFrom || '',
            raw: t
          };
        }
        // Fallback
        return {
          id: t.id,
          date: t.date,
          type: t.type || 'Transaction',
          number: t.no || t.id,
          memo: t.comments || '-',
          dueDate: t.dueDate || '',
          amount: t.total,
          status: t.status || '',
          vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
          account: t.account || '',
          raw: t
        };
      };
      
      // Return all transactions without filtering by vendor status
      // Let VendorPreviewList handle the vendor filtering internally
      const normalized = [
        ...this.bills.map(normalize),
        ...this.payBills.map(pb => normalize({ ...pb, type: 'Pay Bill' })),
        ...this.cheques.map(normalize),
        ...this.vendorCredits.map(normalize),
        ...this.cashPurchases.map(normalize),
        ...this.expenses.map(normalize),
        ...this.cashExpenses.map((row) => normalize({
          ...row,
          expenseKind: 'Cash Expense',
          sourceType: 'cash-expense',
        })),
        ...this.creditCardExpenses.map((row) => normalize({
          ...row,
          expenseKind: 'Credit Card Expense',
          sourceType: 'credit-card-expense',
        })),
      ];
      
      // Cache will be updated by watcher to avoid side effects
      return normalized;
    },
  },
  watch: {
    vendorScope: {
      immediate: true,
      async handler(scope) {
        if (scope === 'all') {
          await this.loadAllVendorsTrackingData();
        } else if (this.selectedVendor?.id) {
          await this.fetchAllTransactions(this.selectedVendor.id);
        } else {
          this.bills = [];
          this.payBills = [];
          this.cashExpenses = [];
          this.creditCardExpenses = [];
        }
      }
    },
    selectedVendor(newVendor) {
      if (this.vendorScope === 'all') {
        return;
      }
      this.cancelPendingRequest();
      this.computedCache = { normalizedTransactions: null, lastDataHash: null };
      if (newVendor) {
        this.fetchAllTransactions(newVendor.id);
      }
    },
    currentView() {
      this.currentPage = 1;
      // Clear cache when view changes (filtering is client-side; no refetch)
      this.computedCache.normalizedTransactions = null;
      this.computedCache.lastDataHash = null;
      this.billTypeFilter = 'All Bills';
    },
    billTypeFilter() {
      this.currentPage = 1;
      // Clear cache when filter changes
      this.computedCache.normalizedTransactions = null;
      this.computedCache.lastDataHash = null;
    },
    '$route.query.vendorId'(vendorId) {
      if (vendorId == null) return;
      const vendor = this.vendors.find((item) => String(item.id) === String(vendorId));
      if (!vendor || String(this.selectedVendor?.id) === String(vendorId)) return;
      if (this.vendorScope !== 'individual') this.vendorScope = 'individual';
      this.selectVendor(vendor);
    },
    dateFilter() {
      this.currentPage = 1;
      // Clear cache when filter changes
      this.computedCache.normalizedTransactions = null;
      this.computedCache.lastDataHash = null;
    },
    searchQuery(newQuery) {
      // Debounce search and reload vendors if query changed
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      this.debounceTimer = setTimeout(() => {
        // Normalize query (trim and handle empty)
        const normalizedQuery = (newQuery || '').trim();
        const normalizedLastQuery = (this.lastSearchQuery !== null ? this.lastSearchQuery : '').trim();
        
        // Always reload if query changed (including empty to non-empty or vice versa)
        // Also reload if lastSearchQuery is null (first time)
        const queryChanged = this.lastSearchQuery === null || normalizedQuery !== normalizedLastQuery;
        
        if (queryChanged) {
          this.lastSearchQuery = normalizedQuery;
          // Clear cache and reload
          this.vendorsCache.clear();
          this.vendorsPage = 1;
          this.vendors = [];
          this.loadVendors(true);
        }
      }, 500); // Increased debounce for server requests
    },
    vendorFilter(newFilter) {
      // Reload vendors when filter changes
      if (newFilter !== this.lastVendorFilter) {
        this.lastVendorFilter = newFilter;
        this.vendorsCache.clear();
        this.vendorsPage = 1;
        this.vendors = [];
        this.loadVendors(true);
      }

      if (this.vendorScope === 'all') {
        this.$nextTick(() => this.loadAllVendorsTrackingData());
      }
    },
    vendorRoleFilter(newRoleFilter) {
      if (newRoleFilter !== this.lastVendorRoleFilter) {
        this.lastVendorRoleFilter = newRoleFilter;
        this.vendorsCache.clear();
        this.vendorsPage = 1;
        this.vendors = [];
        this.loadVendors(true);
      }

      if (this.vendorScope === 'all') {
        this.$nextTick(() => this.loadAllVendorsTrackingData());
      }
    },
    // Watch transaction data to update normalized cache
    bills: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true
    },
    payBills: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true
    },
    cheques: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true
    },
    vendorCredits: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true
    },
    cashPurchases: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true
    },
    expenses: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true
    },
    cashExpenses: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true,
    },
    creditCardExpenses: {
      handler() {
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
      },
      deep: true,
    },
    // Watch normalizedTransactions to cache results
    normalizedTransactions: {
      handler(newVal) {
        if (newVal) {
          const dataHash = JSON.stringify({
            bills: this.bills.map(b => ({ id: b.id, billNo: b.billNo })),
            payBills: this.payBills.map(pb => ({ id: pb.id, no: pb.no })),
            cheques: this.cheques.map(c => ({ id: c.id, chequeNo: c.chequeNo })),
            vendorCredits: this.vendorCredits.map(vc => ({ id: vc.id, creditNoteNo: vc.creditNoteNo })),
            cashPurchases: this.cashPurchases.map(cp => ({ id: cp.id })),
            expenses: this.expenses.map(e => ({ id: e.id })),
            cashExpenses: this.cashExpenses.map(e => ({ id: e.id })),
            creditCardExpenses: this.creditCardExpenses.map(e => ({ id: e.id })),
          });
          this.computedCache.normalizedTransactions = newVal;
          this.computedCache.lastDataHash = dataHash;
        }
      },
      immediate: false
    }
  },
  async created() {
    this.loading = true;
    // Initialize lastSearchQuery if null (first load)
    if (this.lastSearchQuery === null) {
      this.lastSearchQuery = (this.searchQuery || '').trim();
    }
    try {
      await this.loadVendors(true);
      logger.info(`After loading: ${this.vendors.length} total vendors, ${this.filteredVendors.length} filtered vendors`);

      const requestedVendorId = this.$route.query.vendorId;
      const requestedVendor = requestedVendorId != null
        ? this.vendors.find((vendor) => String(vendor.id) === String(requestedVendorId))
        : null;
      if (requestedVendor) {
        this.selectVendor(requestedVendor);
      } else if (this.filteredVendors.length > 0) {
        this.selectVendor(this.filteredVendors[0]);
      } else if (this.vendors.length > 0) {
        // If no filtered vendors but we have vendors, select first one anyway
        logger.warn('No vendors match current filter, selecting first vendor anyway');
        this.selectVendor(this.vendors[0]);
      }
      
      // Fetch all company accounts for the company
      const companyId = this.$route.params.id;
      const res = await ApiService.getCompanyAccounts(companyId);
      this.accounts = res.data.accounts || res.data || [];
      this.accountsMap = {};
      for (const acc of this.accounts) {
        this.accountsMap[acc.id] = acc;
      }
      
      // Start periodic cache cleanup (every 2 minutes)
      this.cacheCleanupInterval = setInterval(() => {
        this.cleanupExpiredCache();
      }, 2 * 60 * 1000);
      this.setupVendorPresence();
    } catch (err) {
      logger.error('Error in created hook:', err);
      this.error = err.message || 'Failed to load vendors';
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport, { passive: true });
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.stopResize);
  },
  methods: {
    setupVendorPresence() {
      if (this.onVendorPresenceEvent) return;
      this.onVendorPresenceEvent = (event) => this.handleVendorPresence(event);
      window.addEventListener('vendor-presence', this.onVendorPresenceEvent);
      import('@/services/companyPresenceClient')
        .then((m) => {
          m.connectCompanyPresence();
          if (typeof m.requestVendorPresence === 'function') {
            m.requestVendorPresence(this.companyId);
          }
        })
        .catch(() => {});
    },
    teardownVendorPresence() {
      if (typeof this.onVendorPresenceEvent === 'function') {
        window.removeEventListener('vendor-presence', this.onVendorPresenceEvent);
        this.onVendorPresenceEvent = null;
      }
    },
    handleVendorPresence(event) {
      const detail = event?.detail || {};
      const eventCompanyId = Number(detail.companyId);
      if (Number.isFinite(eventCompanyId) && eventCompanyId !== this.companyId) return;
      const ids = Array.isArray(detail.onlineVendorIds) ? detail.onlineVendorIds : [];
      this.onlineVendorIds = ids.map(Number).filter((id) => Number.isFinite(id));
    },
    updateViewport() {
      this.viewportWidth = window.innerWidth;
    },
    startResize(e) {
      if (e.button !== 0 || this.isMobileViewport) return;
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartWidth = this.sidemenuWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    onResizeMove(e) {
      if (!this.resizing) return;
      const delta = e.clientX - this.resizeStartX;
      let w = this.resizeStartWidth + delta;
      w = Math.max(this.sidemenuMinWidth, Math.min(this.sidemenuMaxWidth, w));
      this.sidemenuWidth = w;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    // Advanced pagination: Load vendors with server-side pagination (handles 10k+ records)
    async loadVendors(reset = false) {
      // Cancel any pending requests
      this.cancelPendingRequest();
      
      if (reset) {
        // Only reset page to 1 if not explicitly setting a page
        if (!this.loadingSpecificPage) {
          this.vendorsPage = 1;
        }
        this.vendors = [];
        this.hasMoreVendors = true;
        // Don't reset totalVendorsCount when loading specific page
        if (!this.loadingSpecificPage) {
          this.totalVendorsCount = 0;
        }
        this.vendorsCache.clear();
        this.lastVendorFilter = this.vendorFilter;
        // Reset flag after using it
        this.loadingSpecificPage = false;
      }
      
      // Check cache first with TTL validation
      const cacheKey = `${this.vendorsPage}_${this.searchQuery}_${this.vendorFilter}_${this.vendorRoleFilter}`;
      const cached = this.vendorsCache.get(cacheKey);
      
      if (cached) {
        const now = Date.now();
        const isExpired = (now - cached.timestamp) > cached.ttl;
        
        if (!isExpired) {
          // Cache hit - use cached data
          if (reset) {
            this.vendors = cached.data;
          } else {
            const existingIds = new Set(this.vendors.map(v => v.id));
            const newVendors = cached.data.filter(v => v && v.id && !existingIds.has(v.id));
            this.vendors = [...this.vendors, ...newVendors];
          }
          this.loading = false;
          this.loadingMoreVendors = false;
          logger.info(`Cache hit for key: ${cacheKey}`);
          return;
        } else {
          // Cache expired - remove it
          this.vendorsCache.delete(cacheKey);
        }
      }
      
      if (this.loadingMoreVendors && !reset) {
        return;
      }
      
      this.loadingMoreVendors = true;
      if (reset) {
        this.loading = true;
      }
      
      // Create new AbortController for this request
      this.currentRequestController = new AbortController();
      
      try {
        const companyId = this.$route.params.id || this.$route.params.companyId;
        
        if (!companyId) {
          logger.error('Company ID is missing from route params');
          this.error = 'Company ID is required';
          return;
        }
        
        logger.info(`Loading vendors page ${this.vendorsPage} for company: ${companyId}`);
        
        // Build query parameters for paginated request
        const params = new URLSearchParams({
          companyId: companyId,
          page: this.vendorsPage.toString(),
          limit: this.vendorsPerPage.toString()
        });
        
        // Add search parameter only if provided and not empty
        const searchQuery = (this.searchQuery || '').trim();
        if (searchQuery && searchQuery.length > 0) {
          params.append('search', searchQuery);
        }
        
        // Add active filter if not 'All'
        if (this.vendorFilter === 'Active') {
          params.append('active', 'true');
        } else if (this.vendorFilter === 'Inactive') {
          params.append('active', 'false');
        }
        if (this.vendorRoleFilter) {
          params.append('vendorRole', this.vendorRoleFilter);
        }
        // If 'All' is selected, don't add active filter (show all vendors)
        
        // Make paginated API call with retry logic
        const response = await this.retryRequest(async () => {
          return await ApiService.get(`/vendor/vendors?${params.toString()}`, {
            signal: this.currentRequestController.signal
          });
        });
        
        // Handle paginated response
        let newVendors = [];
        let paginationInfo = null;
        
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          // Paginated response structure
          newVendors = response.data.data;
          paginationInfo = response.data.pagination;
          this.totalVendorsCount = paginationInfo?.total || 0;
          this.hasMoreVendors = paginationInfo?.hasMore !== false && newVendors.length === this.vendorsPerPage;
        } else if (Array.isArray(response.data)) {
          // Non-paginated response (fallback)
          newVendors = response.data;
          this.totalVendorsCount = newVendors.length;
          this.hasMoreVendors = false;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          newVendors = response.data.data;
          this.totalVendorsCount = response.data.pagination?.total || newVendors.length;
          this.hasMoreVendors = false;
        }
        
        // Normalize vendor data
        newVendors = newVendors.map(vendor => ({
          ...vendor,
          vendorName: vendor.vendorName || vendor.name || '',
          active: vendor.active !== undefined ? vendor.active : true
        }));
        
        // Cache the results with TTL
        this.vendorsCache.set(cacheKey, {
          data: newVendors,
          timestamp: Date.now(),
          ttl: this.cacheTTL
        });
        
        // Cleanup expired cache entries
        this.cleanupExpiredCache();
        
        // Merge with existing vendors (avoid duplicates)
        if (reset) {
          this.vendors = newVendors;
        } else {
          const existingIds = new Set(this.vendors.map(v => v.id));
          const uniqueNewVendors = newVendors.filter(v => v && v.id && !existingIds.has(v.id));
          this.vendors = [...this.vendors, ...uniqueNewVendors];
        }
        
        // Update page for next load (only for infinite scroll, not for specific page navigation)
        if (this.hasMoreVendors && !reset) {
          this.vendorsPage++;
        }
        
        logger.info(`Loaded ${newVendors.length} vendors (page ${reset ? 1 : this.vendorsPage - 1}). Total: ${this.vendors.length}/${this.totalVendorsCount}`);
        
        // Clear any previous errors
        this.error = '';
      } catch (err) {
        // Don't show error if request was aborted
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          logger.info('Vendor load request was cancelled');
          return;
        }
        
        logger.error('Error loading vendors:', err);
        this.error = err.response?.data?.error || err.message || 'Failed to load vendors';
        
        // If first page fails, try non-paginated endpoint as fallback
        if (reset && this.vendorsPage === 1) {
          try {
            logger.info('Trying fallback non-paginated endpoint');
            const companyId = this.$route.params.id || this.$route.params.companyId;
            const data = await ApiService.getAllVendors(companyId);
            
            let fallbackVendors = [];
            if (Array.isArray(data)) {
              fallbackVendors = data;
            } else if (data && Array.isArray(data.data)) {
              fallbackVendors = data.data;
            }
            
            if (fallbackVendors.length > 0) {
              this.vendors = fallbackVendors.map(vendor => ({
                ...vendor,
                vendorName: vendor.vendorName || vendor.name || '',
                active: vendor.active !== undefined ? vendor.active : true
              }));
              this.totalVendorsCount = this.vendors.length;
              this.hasMoreVendors = false;
              logger.info(`Fallback loaded ${this.vendors.length} vendors`);
              this.error = '';
            }
          } catch (fallbackErr) {
            logger.error('Fallback also failed:', fallbackErr);
          }
        }
      } finally {
        this.loadingMoreVendors = false;
        this.loading = false;
        this.currentRequestController = null;
        this.clearSelectedVendorIfFilteredOut();
      }
    },
    // Handle load more (infinite scroll)
    handleLoadMoreVendors() {
      if (!this.loadingMoreVendors && this.hasMoreVendors) {
        this.loadVendors(false);
      }
    },
    // Handle page change for vendor list pagination
    handleVendorPageChange(page) {
      if (page && page !== this.vendorsPage && page >= 1) {
        // Set flag to prevent page reset in loadVendors
        this.loadingSpecificPage = true;
        this.vendorsPage = page;
        this.loadVendors(true); // Reset data but keep the page number
      }
    },
    // Cleanup expired cache entries and limit cache size
    cleanupExpiredCache() {
      const now = Date.now();
      const expiredKeys = [];
      
      // Find expired entries
      for (const [key, value] of this.vendorsCache.entries()) {
        if ((now - value.timestamp) > value.ttl) {
          expiredKeys.push(key);
        }
      }
      
      // Remove expired entries
      expiredKeys.forEach(key => this.vendorsCache.delete(key));
      
      // If cache is still too large, remove oldest entries
      if (this.vendorsCache.size > this.maxCacheSize) {
        const entries = Array.from(this.vendorsCache.entries())
          .sort((a, b) => a[1].timestamp - b[1].timestamp);
        
        const toRemove = entries.slice(0, this.vendorsCache.size - this.maxCacheSize);
        toRemove.forEach(([key]) => this.vendorsCache.delete(key));
        
        logger.info(`Cache size limit reached, removed ${toRemove.length} oldest entries`);
      }
      
      if (expiredKeys.length > 0) {
        logger.info(`Cleaned up ${expiredKeys.length} expired cache entries`);
      }
    },
    // Retry mechanism with exponential backoff
    async retryRequest(requestFn, retries = this.maxRetries) {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          return await requestFn();
        } catch (error) {
          if (attempt === retries - 1) throw error;
          
          // Exponential backoff: 1s, 2s, 4s
          const delay = this.retryDelay * Math.pow(2, attempt);
          logger.warn(`Request failed, retrying in ${delay}ms (attempt ${attempt + 1}/${retries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    },
    // Performance: Cancel pending requests
    cancelPendingRequest() {
      if (this.currentRequestController) {
        this.currentRequestController.abort();
        this.currentRequestController = null;
      }
    },
    selectVendor(vendor) {
      if (this.vendorScope === 'all') {
        return;
      }
      this.computedCache = { normalizedTransactions: null, lastDataHash: null };
      this.selectedVendor = vendor;
    },
    async loadAllVendorsTrackingData() {
      this.cancelPendingRequest();
      this.currentRequestController = new AbortController();
      this.loadingBills = true;
      this.loadingPayBills = true;
      try {
        const companyId = this.$route.params.id || this.$route.params.companyId;
        const allowedIds = new Set(this.filteredVendors.map((v) => String(v.id)));
        const signal = this.currentRequestController.signal;

        const [billsResponse, payBillsResponse] = await Promise.all([
          ApiService.get(`/vendor/enter-bill/company/${companyId}/bills-expenses`, { signal }),
          ApiService.get(`/vendor/pay-bill/company/${companyId}`, { signal })
        ]);

        const billsPayload = billsResponse.data;
        const payBillsRaw = payBillsResponse.data?.data ?? payBillsResponse.data;

        const billRows = Array.isArray(billsPayload)
          ? billsPayload
          : (Array.isArray(billsPayload?.data) ? billsPayload.data : []);

        this.bills = billRows
          .filter((row) => row && row.type === 'Bill')
          .filter((row) => {
            const vid = row.vendorId ?? row.vendor_id;
            return vid != null && allowedIds.has(String(vid));
          })
          .map((row) => ({
            id: row.id,
            billNo: row.no || row.billNo,
            date: row.date,
            dueDate: row.dueDate,
            total: row.originalAmount ?? row.total,
            status: row.status,
            vendorId: row.vendorId,
            vendorName: row.vendorName,
            comments: row.comments || row.memo,
            balance: row.balance,
            paidAmount: row.paidAmount
          }));

        const payList = Array.isArray(payBillsRaw)
          ? payBillsRaw
          : (Array.isArray(payBillsRaw?.data) ? payBillsRaw.data : []);

        this.payBills = payList.filter((row) => {
          const vid = row.vendorId ?? row.vendor_id ?? row.vendor?.id;
          return vid != null && allowedIds.has(String(vid));
        });

        this.cashExpenses = billRows
          .filter((row) => row?.expenseKind === 'Cash Expense')
          .filter((row) => {
            const vid = row.vendorId ?? row.vendor_id;
            return vid != null && allowedIds.has(String(vid));
          })
          .map((row) => ({
            id: row.id,
            vendorId: row.vendorId,
            number: row.no,
            date: row.date,
            total: row.originalAmount ?? row.total,
            comments: row.comments || '',
            payFrom: row.payFrom,
            expenseKind: 'Cash Expense',
            sourceType: 'cash-expense',
          }));

        this.creditCardExpenses = billRows
          .filter((row) => row?.expenseKind === 'Credit Card Expense')
          .filter((row) => {
            const vid = row.vendorId ?? row.vendor_id;
            return vid != null && allowedIds.has(String(vid));
          })
          .map((row) => ({
            id: row.id,
            vendorId: row.vendorId,
            number: row.no,
            date: row.date,
            total: row.originalAmount ?? row.total,
            comments: row.comments || '',
            payFrom: row.payFrom,
            expenseKind: 'Credit Card Expense',
            sourceType: 'credit-card-expense',
          }));

        this.computedCache = {
          normalizedTransactions: null,
          lastDataHash: null
        };
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        logger.error('Error loading all vendors tracking data:', error);
        this.bills = [];
        this.payBills = [];
        this.cashExpenses = [];
        this.creditCardExpenses = [];
      } finally {
        this.loadingBills = false;
        this.loadingPayBills = false;
        this.currentRequestController = null;
      }
    },
    handleVendorScopeChange(scope) {
      if (scope === this.vendorScope) return;
      this.vendorScope = scope;
      this.currentPage = 1;
      this.computedCache = { normalizedTransactions: null, lastDataHash: null };
      if (scope === 'all') {
        this.globalSearchLabel = '';
        this.globalSearchVendorId = null;
        this.selectedVendor = null;
      } else if (scope === 'individual') {
        this.globalSearchLabel = '';
        this.globalSearchVendorId = null;
        if (this.selectedVendor) {
          this.selectVendor(this.selectedVendor);
        } else if (this.filteredVendors.length > 0) {
          this.selectVendor(this.filteredVendors[0]);
        }
      }
    },
    handleGlobalSearchSelected({ type, id, name }) {
      if (type !== 'vendor' || id == null) return;
      this.globalSearchVendorId = String(id);
      this.globalSearchLabel = name ? `Vendor: ${name}` : '';
      this.searchQuery = '';
      this.currentPage = 1;
      this.computedCache.normalizedTransactions = null;
      this.computedCache.lastDataHash = null;

      let vendor = this.vendors.find((v) => String(v.id) === String(id));
      if (!vendor) {
        vendor = {
          id,
          vendorName: name || 'Vendor',
          vendorId: '',
          active: true
        };
        this.vendors = [vendor, ...this.vendors];
      }
      this.selectedVendor = vendor;
      if (this.vendorScope === 'all') {
        return;
      }
      this.selectVendor(vendor);
    },
    handleGlobalSearchCleared() {
      this.globalSearchLabel = '';
      this.globalSearchVendorId = null;
      if (this.vendorScope === 'all') {
        this.selectedVendor = null;
      }
      this.computedCache.normalizedTransactions = null;
      this.computedCache.lastDataHash = null;
    },
    handleVendorSelected(vendorId) {
      this.globalSearchLabel = '';
      const vendor = this.vendors.find((v) => String(v.id) === String(vendorId));
      if (!vendor) return;
      if (this.vendorScope === 'all') {
        this.globalSearchVendorId = String(vendor.id);
        this.selectedVendor = vendor;
        this.currentPage = 1;
        this.computedCache.normalizedTransactions = null;
        this.computedCache.lastDataHash = null;
        return;
      }
      this.$nextTick(() => this.selectVendor(vendor));
    },
    handleFilterChanged(newFilter) {
      this.vendorFilter = newFilter;
    },
    handleVendorRoleFilterChanged(newRoleFilter) {
      this.vendorRoleFilter = newRoleFilter;
    },
    clearSelectedVendorIfFilteredOut() {
      if (this.loading || this.loadingMoreVendors || this.vendors.length === 0) {
        return;
      }
      if (
        this.selectedVendor &&
        !this.filteredVendors.some((v) => v.id === this.selectedVendor.id)
      ) {
        this.selectedVendor = null;
        this.bills = [];
        this.payBills = [];
        this.cashExpenses = [];
        this.creditCardExpenses = [];
      }
    },
    handleSearchChanged(searchQuery) {
      // Debounce is handled in watcher
      this.searchQuery = searchQuery;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
    },
    handleViewFilterChanged(viewFilter) {
      // Convert internal component filter value back to user-friendly string
      switch (viewFilter) {
        case 'all': this.currentView = 'All Transactions'; break;
        case 'Bill': this.currentView = 'Bills'; break;
        case 'Pay Bill': this.currentView = 'Pay Bills'; break;
        case 'Cash Purchase': this.currentView = 'Cash Purchases'; break;
        case 'Cheque': this.currentView = 'Cheques'; break;
        case 'Vendor Credit Note': this.currentView = 'Vendor Credit Notes'; break;
        case 'Expense': this.currentView = 'Expenses'; break;
        default: this.currentView = 'All Transactions';
      }
    },
    handleDateFilterChanged(dateFilter) {
      // Convert internal component filter value back to user-friendly string
      switch (dateFilter) {
        case 'all': this.dateFilter = 'All'; break;
        case 'this_week': this.dateFilter = 'This week'; break;
        case 'this_month': this.dateFilter = 'This month'; break;
        case 'last_week': this.dateFilter = 'Last week'; break;
        case 'last_month': this.dateFilter = 'Last month'; break;
        case 'this_financial_year': this.dateFilter = 'This financial year'; break;
        case 'last_financial_year': this.dateFilter = 'Last financial year'; break;
        case 'this_financial_quarter': this.dateFilter = 'This financial quarter'; break;
        case 'last_financial_quarter': this.dateFilter = 'Last financial quarter'; break;
        case 'financial_year_to_date': this.dateFilter = 'Financial year to date'; break;
        default: this.dateFilter = 'All';
      }
    },
    handleTypeFilterChanged(typeFilter) {
      // Convert internal component filter value to match the new status format
      // For Bills: New format uses direct status values: 'Paid', 'Draft', 'Open', 'Overdue'
      // For Pay Bills: Payment methods: 'Check', 'Cash', 'Credit Card', 'Bank Transfer', 'Direct Deposit'
      if (typeFilter === 'all') {
        this.billTypeFilter = 'All Bills';
      } else if (['Draft', 'Open', 'Overdue', 'Paid', 'Partial', 'Cancelled', 'Disputed', 'On Hold'].includes(typeFilter)) {
        // Direct status value for Bills - store it as is for filtering
        this.billTypeFilter = typeFilter;
      } else if (['Check', 'Cash', 'Credit Card', 'Bank Transfer', 'Direct Deposit'].includes(typeFilter)) {
        // Payment method for Pay Bills - store it as is for filtering
        this.billTypeFilter = typeFilter;
      } else {
        // Legacy format mapping for backward compatibility
        const statusMap = {
          'Draft Bills': 'Draft',
          'Open Bills': 'Open',
          'Overdue Bills': 'Overdue',
          'Paid Bills': 'Paid'
        };
        this.billTypeFilter = statusMap[typeFilter] || 'All Bills';
      }
    },
    getViewFilterValue(viewFilter) {
      // Convert user-friendly filter string to internal component-friendly value
      switch (viewFilter) {
        case 'All Transactions': return 'all';
        case 'Bills': return 'Bill';
        case 'Pay Bills': return 'Pay Bill';
        case 'Cash Purchases': return 'Cash Purchase';
        case 'Cheques': return 'Check';
        case 'Vendor Credit Notes': return 'Vendor Credit Note';
        case 'Expenses': return 'Expense';
        default: return 'all';
      }
    },
    getDateFilterValue(dateFilter) {
      // Convert user-friendly filter string to internal component-friendly value
      switch (dateFilter) {
        case 'All': return 'all';
        case 'This week': return 'this_week';
        case 'This month': return 'this_month';
        case 'Last week': return 'last_week';
        case 'Last month': return 'last_month';
        case 'This financial year': return 'this_financial_year';
        case 'Last financial year': return 'last_financial_year';
        case 'This financial quarter': return 'this_financial_quarter';
        case 'Last financial quarter': return 'last_financial_quarter';
        case 'Financial year to date': return 'financial_year_to_date';
        default: return 'all';
      }
    },
    getTypeFilterValue(typeFilter) {
      // Convert filter value to match VendorPreviewList format
      // For Bills: direct status values ('Paid', 'Draft', 'Open', 'Overdue')
      // For Pay Bills: payment methods ('Check', 'Cash', 'Credit Card', 'Bank Transfer', 'Direct Deposit')
      if (typeFilter === 'All Bills') {
        return 'all';
      } else if (['Draft', 'Open', 'Overdue', 'Paid', 'Partial', 'Cancelled', 'Disputed', 'On Hold'].includes(typeFilter)) {
        // Direct status value for Bills - return as is
        return typeFilter;
      } else if (['Check', 'Cash', 'Credit Card', 'Bank Transfer', 'Direct Deposit'].includes(typeFilter)) {
        // Payment method for Pay Bills - return as is
        return typeFilter;
      } else {
        // Legacy format mapping for backward compatibility
        const statusMap = {
          'Draft Bills': 'Draft',
          'All Open Bills': 'Open',
          'Overdue Bills': 'Overdue',
          'Paid Bills': 'Paid'
        };
        return statusMap[typeFilter] || 'all';
      }
    },
    async fetchAllTransactions(vendorId) {
      if (!vendorId) {
        this.bills = [];
        this.payBills = [];
        this.cheques = [];
        this.vendorCredits = [];
        this.cashPurchases = [];
        this.expenses = [];
        this.cashExpenses = [];
        this.creditCardExpenses = [];
        return;
      }
      
      // Cancel any pending request
      this.cancelPendingRequest();
      
      // Create new AbortController for this request
      this.currentRequestController = new AbortController();
      
      try {
        const companyId = this.$route.params.id || this.$route.params.companyId;
        const [billsResponse, payBillsResponse, expenseRows] = await Promise.all([
          ApiService.getVendorBills(vendorId).catch(err => {
            if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
              logger.error('Error fetching vendor bills:', err);
            }
            return [];
          }),
          ApiService.get(`/vendor/pay-bill/vendor/${vendorId}`, {
            signal: this.currentRequestController.signal
          }).then(res => res.data).catch(err => {
            if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
              logger.error('Error fetching pay bills:', err);
            }
            return [];
          }),
          ApiService.getBillsAndExpenses(companyId, {
            view: 'Expenses',
            vendorId: String(vendorId),
          }).catch((err) => {
            if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
              logger.error('Error fetching vendor direct expenses:', err);
            }
            return [];
          }),
        ]);
        
        // Don't update if request was aborted
        if (this.currentRequestController.signal.aborted) {
          return;
        }
        
        this.bills = Array.isArray(billsResponse) ? billsResponse : [];
        this.payBills = Array.isArray(payBillsResponse) ? payBillsResponse : [];

        const expenseList = Array.isArray(expenseRows) ? expenseRows : [];
        this.cashExpenses = expenseList
          .filter((row) => row?.expenseKind === 'Cash Expense')
          .map((row) => ({
            id: row.id,
            vendorId: row.vendorId,
            number: row.no,
            date: row.date,
            total: row.originalAmount ?? row.total,
            comments: row.comments || '',
            payFrom: row.payFrom,
            expenseKind: 'Cash Expense',
            sourceType: 'cash-expense',
          }));
        this.creditCardExpenses = expenseList
          .filter((row) => row?.expenseKind === 'Credit Card Expense')
          .map((row) => ({
            id: row.id,
            vendorId: row.vendorId,
            number: row.no,
            date: row.date,
            total: row.originalAmount ?? row.total,
            comments: row.comments || '',
            payFrom: row.payFrom,
            expenseKind: 'Credit Card Expense',
            sourceType: 'credit-card-expense',
          }));
        this.cheques = [];
        this.vendorCredits = [];
        this.cashPurchases = [];
        this.expenses = [];
      } catch (error) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        logger.error('Error fetching transactions:', error);
        this.bills = [];
        this.payBills = [];
        this.cheques = [];
        this.vendorCredits = [];
        this.cashPurchases = [];
        this.expenses = [];
        this.cashExpenses = [];
        this.creditCardExpenses = [];
      } finally {
        this.currentRequestController = null;
      }
    },
    async handleExportToCsv() {
      await this.loadAndShowTransactions();
    },
    async loadAndShowTransactions() {
      this.loadingTransactions = true;
      this.transactionsError = '';
      this.showTransactionsModal = true;
      
      try {
        const companyId = this.$route.params.id || this.$route.params.companyId;
        
        // If a specific vendor is selected, export only that vendor's transactions
        const vendorsToExport = this.selectedVendor 
          ? [this.selectedVendor] 
          : this.filteredVendors;
        
        const result = await fetchVendorTransactions(companyId, vendorsToExport);
        let transactions = result.allTransactions || [];
        
        // Apply transaction type filters (same logic as filteredTransactions in VendorPreviewList)
        transactions = this.applyTransactionFilters(transactions);
        
        // Sort transactions by date (newest first)
        transactions.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });
        
        this.vendorTransactions = transactions;
      } catch (err) {
        logger.error('Error loading vendor transactions:', err);
        this.transactionsError = 'Failed to load vendor transactions';
        this.vendorTransactions = [];
      } finally {
        this.loadingTransactions = false;
      }
    },
    applyTransactionFilters(transactions) {
      let filtered = [...transactions];
      
      // Filter by transaction view
      if (this.currentView !== 'All Transactions') {
        const viewTypeMap = {
          'Bills': 'Bill',
          'Pay Bills': 'Pay Bill',
          'Cash Purchases': 'Cash Purchase',
          'Cheques': 'Cheque',
          'Vendor Credit Notes': 'Vendor Credit Note',
          'Expenses': 'Expense',
        };
        const typeFilter = viewTypeMap[this.currentView];
        if (typeFilter) {
          filtered = filtered.filter(t => t.type === typeFilter);
        }
      }
      
      // Filter by bill type (for Bills) - using status values from EnterBill model
      // Status values: 'Draft', 'Open', 'Partial', 'Paid', 'Overdue', 'Cancelled', 'Disputed', 'On Hold'
      if (this.currentView === 'Bills' && this.billTypeFilter !== 'All Bills') {
        const validStatuses = ['Draft', 'Open', 'Overdue', 'Paid', 'Partial', 'Cancelled', 'Disputed', 'On Hold'];
        
        // Map old filter values to new status values for backward compatibility
        const statusMap = {
          'Draft Bills': 'Draft',
          'All Open Bills': 'Open',
          'All Overdue Bills': 'Overdue',
          'Paid Bills': 'Paid'
        };
        
        // Get target status (handle both old and new format)
        let targetStatus = statusMap[this.billTypeFilter] || this.billTypeFilter;
        
        if (validStatuses.includes(targetStatus)) {
          filtered = filtered.filter(t => {
            // Must be a Bill transaction
            if (!t || t.type !== 'Bill') {
              return false;
            }
            
            // Get the raw status value
            const rawStatus = t.status;
            
            // Skip if status is missing or invalid
            if (!rawStatus || rawStatus === '-' || rawStatus === '' || rawStatus === null || rawStatus === undefined) {
              return false;
            }
            
            // Normalize both values for case-insensitive comparison
            const normalizedStatus = String(rawStatus).toLowerCase().trim();
            const normalizedTarget = String(targetStatus).toLowerCase().trim();
            
            // Check if status matches the filter
            return normalizedStatus === normalizedTarget;
          });
        }
      }
      
      // Filter by payment method (for Pay Bills)
      // Payment methods: 'Check', 'Cash', 'Credit Card', 'Bank Transfer', 'Direct Deposit'
      if (this.currentView === 'Pay Bills' && this.billTypeFilter && this.billTypeFilter !== 'All Bills') {
        const validPaymentMethods = ['Check', 'Cash', 'Credit Card', 'Bank Transfer', 'Direct Deposit'];
        
        // Check if the filter is a valid payment method
        if (validPaymentMethods.includes(this.billTypeFilter)) {
          filtered = filtered.filter(t => {
            // Must be a Pay Bill transaction
            if (!t || t.type !== 'Pay Bill') {
              return false;
            }
            
            // Get payment method from transaction (check multiple possible field names)
            const paymentMethod = (t.paymentMethod || t.payMethod || 'Cash').trim();
            const normalizedPaymentMethod = String(paymentMethod).trim();
            const normalizedFilter = String(this.billTypeFilter).trim();
            
            // Check if payment method matches the filter
            return normalizedPaymentMethod === normalizedFilter;
          });
        }
      }
      
      // Filter by date
      if (this.dateFilter !== 'All') {
        filtered = this.filterByDate(filtered, this.dateFilter);
      }
      
      return filtered;
    },
    filterByDate(transactions, dateFilter) {
      if (!dateFilter || dateFilter === 'All' || !transactions || !Array.isArray(transactions)) {
        return transactions || [];
      }
      
      const now = new Date();
      let startDate, endDate;
      
      if (dateFilter === 'This week') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 7);
      } else if (dateFilter === 'Last week') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      } else if (dateFilter === 'This month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else if (dateFilter === 'Last month') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      } else if (dateFilter === 'This financial year') {
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear() + 1, 0, 0);
      } else if (dateFilter === 'Last financial year') {
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear(), 0, 0);
      } else if (dateFilter === 'This financial quarter') {
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
      } else if (dateFilter === 'Last financial quarter') {
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), (quarter - 1) * 3, 1);
        endDate = new Date(now.getFullYear(), quarter * 3, 0);
      } else if (dateFilter === 'Financial year to date') {
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = now;
      }

      return transactions.filter(txn => {
        if (!txn || !txn.date) return false;
        try {
          const txnDate = new Date(txn.date);
          return txnDate >= startDate && txnDate <= endDate;
        } catch (error) {
          logger.warn('Error parsing transaction date:', error);
          return false;
        }
      });
    },
    closeTransactionsModal() {
      this.showTransactionsModal = false;
      this.vendorTransactions = [];
      this.transactionsError = '';
    },
    async confirmExportToExcel() {
      try {
        // If a specific vendor is selected, export only that vendor
        const vendorsToExport = this.selectedVendor 
          ? [this.selectedVendor] 
          : this.filteredVendors;
        
        await exportVendorsWithTransactionsToExcel({
          vendors: vendorsToExport,
          transactions: this.vendorTransactions,
          accountsMap: this.accountsMap,
          showToast: (type, message) => {
            if (this.$toast) {
              this.$toast[type](message);
            }
          }
        });
        this.closeTransactionsModal();
      } catch (err) {
        logger.error('Error exporting vendors with transactions to Excel:', err);
        // Error is already handled in the export function
      }
    },
    toggleSideMenu() {
      const willShow = !this.showSideMenu;
      this.showSideMenu = willShow;
      if (willShow && this.vendorScope === 'all') {
        this.vendorScope = 'individual';
        const vendor =
          this.selectedVendor
          ?? (this.filteredVendors.length > 0 ? this.filteredVendors[0] : null);
        if (vendor) {
          this.$nextTick(() => this.selectVendor(vendor));
        }
      }
    },
    hideSideMenu() {
      this.showSideMenu = false;
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateViewport);
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.stopResize);
    // Clean up debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    // Cancel any pending requests
    this.cancelPendingRequest();
    // Clear cache cleanup interval
    if (this.cacheCleanupInterval) {
      clearInterval(this.cacheCleanupInterval);
    }
    // Final cache cleanup
    this.cleanupExpiredCache();
    this.teardownVendorPresence();
  }
}
</script>